/**
 * Bid-amount parsing and validation — the pure half of the phone's `<BidBar>`
 * (P2.1) and of the website's bid panel.
 *
 * IT LIVES IN `@auctisync/shared` so BOTH surfaces reach the same code. It
 * started in `@auctisync/ui-native` and moved here on 2026-09-03, when the web
 * lot page grew the same four quick amounts: a website cannot import a React
 * Native package, and the only other way to draw those tiles was to retype the
 * ladder on the money path.
 *
 * ⚠️ MONEY CODE. Two rules govern everything here:
 *
 *  1. **No floats.** `parseFloat('12.34') * 100` is `1233.9999999999998`. Cents
 *     are derived by STRING manipulation so a bid is exact by construction, not
 *     exact because a rounding call happened to save us. BUILD_PLAN's "money is
 *     always integer cents, never floats" applies to parsing too.
 *
 *  2. **Never silently change the bidder's number.** An ambiguous or
 *     over-precise entry is REJECTED, not rounded. Quietly turning `12.345`
 *     into `$12.35` alters what someone committed to pay, and on a proxy bid
 *     that ceiling is a binding maximum.
 *
 * This module decides nothing about whether a bid WINS — the `place_bid` RPC is
 * the only authority on that. It only catches what can be caught before a
 * round trip, so an obviously-doomed bid costs no network and no suspense.
 */

/** Why an entry was refused. */
export type BidInputError = 'empty' | 'invalid' | 'below_minimum';

export type BidInputResult =
  | { ok: true; cents: number }
  | { ok: false; error: BidInputError; cents: number | null };

/**
 * Parses a typed amount into integer cents, or `null` if it is not a number we
 * are willing to act on.
 *
 * Accepts what people actually type: `1200`, `$1,200`, `1200.00`, ` 1200 `.
 * Rejects negatives, more than two decimal places, multiple decimal points,
 * and anything containing other characters.
 *
 * Returns `null` rather than throwing — the caller renders a message, and an
 * exception on every keystroke-adjacent call site would be its own bug source.
 */
export function parseBidToCents(raw: string): number | null {
  const cleaned = raw.trim().replace(/^\$/, '');
  if (cleaned === '') return null;

  // Commas are validated in PLACE before being removed, never blanket-stripped.
  // Blindly deleting them reads a comma-as-decimal-separator entry ("12,34",
  // meaning 12.34 in much of the world) as "1234" — a 100x error on what is a
  // binding proxy ceiling. A comma is therefore only accepted as a thousands
  // separator: 1-3 leading digits then strictly 3-digit groups.
  //   whole part  → 1200   or   1,200   or   1,234,567
  //   fraction    → optional, 1-2 digits
  const match = /^(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) return null;

  const whole = (match[1] ?? '').replace(/,/g, '');
  // Pad so `.5` and `.50` both mean fifty cents; a single digit is tens of cents.
  const fraction = (match[2] ?? '').padEnd(2, '0');

  const cents = Number(whole) * 100 + Number(fraction);
  // Beyond 2^53 the arithmetic stops being exact. No real bid reaches this;
  // a paste of a 20-digit number does.
  if (!Number.isSafeInteger(cents)) return null;
  return cents;
}

/**
 * Full check for a typed bid against the lot's next allowed amount.
 *
 * `nextMinCents` comes from the server (`next_min_cents` on the `place_bid`
 * result, or the lot projection). It already accounts for the increment ladder,
 * so this compares against it rather than recomputing increments client-side —
 * a second implementation of the ladder would be a second thing to disagree.
 */
export function validateBidInput(raw: string, nextMinCents: number): BidInputResult {
  if (raw.trim() === '') return { ok: false, error: 'empty', cents: null };

  const cents = parseBidToCents(raw);
  if (cents === null) return { ok: false, error: 'invalid', cents: null };

  // A non-finite or absent minimum must not become an accidental floor of 0.
  // Treat it as "cannot check locally" and let the server decide.
  if (!Number.isFinite(nextMinCents)) return { ok: true, cents };

  if (cents < nextMinCents) return { ok: false, error: 'below_minimum', cents };
  return { ok: true, cents };
}

/**
 * The message shown under the field.
 *
 * Phrased as what to do, not what went wrong: a bidder mid-auction wants the
 * number they should type, not a diagnosis.
 */
export function bidInputMessage(
  error: BidInputError,
  nextMinCents: number,
  formatCents: (cents: number) => string,
): string {
  switch (error) {
    case 'empty':
      return `Enter an amount — ${formatCents(nextMinCents)} or more.`;
    case 'invalid':
      return 'Enter a whole amount in dollars and cents, like 1200 or 1200.50.';
    case 'below_minimum':
      return `The next bid must be at least ${formatCents(nextMinCents)}.`;
  }
}

/**
 * The four quick amounts on the bid sheet: the minimum, then three steps above
 * it.
 *
 * WHY OFFSETS FROM THE MINIMUM RATHER THAN LADDER STEPS. These set a bidder's
 * MAXIMUM, not the amount that gets posted. A ceiling does not have to land on
 * an increment boundary — the server bids the least it needs to and stops, so
 * any figure at or above the minimum is a legal ceiling. Trying to align these
 * to the lot's increment ladder would recompute, client-side, the one thing
 * `nextMinCents` exists to stop us recomputing.
 *
 * The point of the tiles is speed: four taps that are all certainly valid, so a
 * bidder in the last thirty seconds never has to think about arithmetic.
 */
export interface BidTile {
  /** What the bidder sees. */
  label: string;
  cents: number;
}

/** Steps above the minimum, from the design's bid sheet. */
const TILE_STEPS_CENTS = [500, 2000, 5000] as const;

export function bidIncrementTiles(
  nextMinCents: number,
  // Injected, matching `bidInputMessage` above — this module stays free of
  // @auctisync/shared so it can be unit-tested without the currency layer.
  formatCents: (cents: number) => string,
): BidTile[] {
  if (!Number.isFinite(nextMinCents) || nextMinCents <= 0) return [];
  return [
    { label: 'Minimum', cents: nextMinCents },
    ...TILE_STEPS_CENTS.map((step) => ({
      label: `+${formatCents(step)}`,
      cents: nextMinCents + step,
    })),
  ];
}

/**
 * One step of the −/+ steppers beside the amount field.
 *
 * Never below the minimum: a stepper that walks a bidder down to an amount the
 * server will refuse is a control that manufactures its own rejection.
 */
export function stepBid(currentCents: number, direction: 1 | -1, nextMinCents: number): number {
  const step = TILE_STEPS_CENTS[0];
  const next = currentCents + direction * step;
  return Math.max(next, nextMinCents);
}
